import React, {
  HTMLAttributes,
  SelectHTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';
import classnames from 'classnames';

import styles from './SelectInput.module.scss';

export interface SelectProps
  extends HTMLAttributes<HTMLElement>,
    Pick<SelectHTMLAttributes<HTMLSelectElement>, 'name' | 'disabled'> {
  options: Option[];
  hasError: boolean;
}

export interface Option {
  value: string;
  label: string;
}

const SelectInput: React.FC<SelectProps> = (props) => {
  const {
    className,
    name,
    options,
    placeholder,
    hasError,
    disabled,
    onChange = () => {},
    onBlur = () => {},
    ...otherProps
  } = props;

  const [showOptionList, setShowOptionList] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState('');

  const handleSelectClick = useCallback((e) => {
    setShowOptionList((prev) => !prev);
  }, []);

  const handleOptionClick = useCallback(
    (e, option) => {
      setSelectedOptions(option.label);
      onChange(option.value);
      setShowOptionList(false);
      onBlur(e);
    },
    [onBlur, onChange]
  );

  const handleClickOutside = useCallback((e) => {
    if (
      !e.target.classList.contains(styles.option) &&
      !e.target.classList.contains(styles.selectbox)
    ) {
      setShowOptionList(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const rootClassName = classnames(styles.root, className, {
    [styles.filled]: selectedOptions && !showOptionList,
    [styles.error]: hasError,
    [styles.disabled]: disabled,
    [styles.listOpen]: showOptionList,
  });

  const iconClassName = classnames(styles.icon, 'fas', 'fa-sort');

  return (
    <div className={rootClassName}>
      <div
        className={styles.selectbox}
        onClick={handleSelectClick}
        onKeyDown={() => {}}
        tabIndex={0}
        role='button'
        {...otherProps}
      >
        {selectedOptions || placeholder}
        <span className={iconClassName}></span>
      </div>
      {showOptionList && (
        <ul className={styles.list}>
          {options.map((option: Option) => (
            <li className={styles.listItem} key={option.value}>
              <button className={styles.option} onClick={(e) => handleOptionClick(e, option)}>
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SelectInput;
